import React, { useMemo } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const ELLIPSIS = "ellipsis";

const range = (start, end) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, i) => start + i);
};

const usePagination = ({ totalPages, page, siblingCount = 1, boundaryCount = 1 }) => {
  return useMemo(() => {
    if (totalPages <= 0) return [];

    const totalNumbers = siblingCount * 2 + boundaryCount * 2 + 3;
    const totalBlocks = totalNumbers + 2;

    if (totalPages <= totalBlocks) {
      return range(0, totalPages - 1);
    }

    const startPages = range(0, boundaryCount - 1);
    const endPages = range(totalPages - boundaryCount, totalPages - 1);

    const siblingsStart = Math.max(
      Math.min(page - siblingCount, totalPages - boundaryCount - siblingCount * 2 - 1),
      boundaryCount
    );

    const siblingsEnd = Math.min(
      Math.max(page + siblingCount, boundaryCount + siblingCount * 2),
      totalPages - boundaryCount - 1
    );

    const showStartEllipsis = siblingsStart > boundaryCount;
    const showEndEllipsis = siblingsEnd < totalPages - boundaryCount - 1;

    const items = [];

    items.push(...startPages);

    if (showStartEllipsis) {
      items.push(ELLIPSIS);
    } else {
      items.push(...range(boundaryCount, siblingsStart - 1));
    }

    items.push(...range(siblingsStart, siblingsEnd));

    if (showEndEllipsis) {
      items.push(ELLIPSIS);
    } else {
      items.push(...range(siblingsEnd + 1, totalPages - boundaryCount - 1));
    }

    items.push(...endPages);

    return items;
  }, [totalPages, page, siblingCount, boundaryCount]);
};

const Pagination = ({ page, pageSize, total, onPageChange, itemLabel = "items" }) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const start = total === 0 ? 0 : page * pageSize + 1;
  const end = Math.min(total, (page + 1) * pageSize);

  const items = usePagination({ totalPages, page, siblingCount: 1, boundaryCount: 1 });

  const canPrev = page > 0;
  const canNext = page < totalPages - 1;

  return (
    <div className="um-pagination">
      <div className="um-pagination__info">
        Showing {start} to {end} of {total} {itemLabel}
      </div>

      <div className="um-pagination__controls">
        <button
          type="button"
          className="um-pagebtn um-pagebtn--nav"
          onClick={() => onPageChange(page - 1)}
          disabled={!canPrev}
          aria-label="Previous page"
        >
          <FaChevronLeft />
        </button>

        {items.map((it, idx) => {
          if (it === ELLIPSIS) {
            return (
              <span key={`e-${idx}`} className="um-pageellipsis" aria-hidden="true">
                …
              </span>
            );
          }

          const p = it;

          return (
            <button
              key={p}
              type="button"
              className={p === page ? "um-pagebtn um-pagebtn--active" : "um-pagebtn"}
              onClick={() => onPageChange(p)}
              aria-label={`Page ${p + 1}`}
            >
              {p + 1}
            </button>
          );
        })}

        <button
          type="button"
          className="um-pagebtn um-pagebtn--nav"
          onClick={() => onPageChange(page + 1)}
          disabled={!canNext}
          aria-label="Next page"
        >
          <FaChevronRight />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
